export type NullableProps<P extends Record<string, any>, T extends keyof P> = Omit<P, T> & {
  [Key in T]?: P[Key];
};

/**
 * Picks defined fields from an object and maintains optional state of fields.
 *
 * ---
 *
 * #### Example
 *
 * ```
 * // Interface with a variety of optional fields.
 * interface Example {
 *   foo: string;
 *   bar: number | undefined;
 *   baz?: any[];
 * }
 *
 * // Missing fields are allowed and get ignored.
 * type Fields = keyof Example | 'missing-field';
 * type PickedExample = PickExactFields<Example, Fields>;
 *
 * const obj = {} as PickedExample;
 * // obj.foo: string
 * // obj.bar: number | undefined
 * // obj.baz?: any[] | undefined
 * ```
 */
export type PickExactFields<T, Fields extends string> = {
  [Key in Fields]?: Key extends keyof T ? T[Key] : never;
} & {
  [Key in keyof T as Extract<Key, Fields>]: Key extends Fields ? T[Key] : never;
};

/**
 * Helper function that allows proper type checking of switch cases.
 *
 * Provide no `default` case and return this after.
 */
export const assertNever = <ProdFallback = never>(
  x: never,
  prodFallback: ProdFallback = undefined as never
): ProdFallback => {
  if (process.env.NODE_ENV === 'production') {
    return prodFallback;
  }
  throw new Error(`Unexpected object: ${x}`);
};

export type ArrayElement<T> = T extends (infer U)[] ? U : never;

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void
  ? I
  : never;
type UnionToOvlds<U> = UnionToIntersection<U extends any ? (f: U) => void : never>;

type PopUnion<U> = UnionToOvlds<U> extends (a: infer A) => void ? A : never;

export type UnionConcat<U extends string, Sep extends string> = PopUnion<U> extends infer SELF
  ? SELF extends string
    ? Exclude<U, SELF> extends never
      ? SELF
      :
          | `${UnionConcat<Exclude<U, SELF>, Sep>}${Sep}${SELF}`
          | UnionConcat<Exclude<U, SELF>, Sep>
          | SELF
    : never
  : never;
